import TrackRollAction from 'midi/TrackRollAction.js';
import ResolutionAction from 'midi/ResolutionAction.js';
import ResolutionChangeEvent from 'session/ResolutionChangeEvent.js';
import {SEQUENCE_ROLL} from 'session/constants.js';
import SequenceRollEvent from 'session/SequenceRollEvent.js';
import SequenceRollAction from 'midi/SequenceRollAction.js';
import {MAP, DELETE, OFF} from 'constants/midi-map-modes.js';
import {publishRoll, publishTrigger} from 'messaging/messages.js';
import {
    setResolution,
    setActionMidiMapSelection,
    setTrackRolling,
} from 'actions/index.js';
 

export default superclass => class extends superclass  {

    get rolling() {
        return this.store.getState().sequencer.trackRolling;
    }

    set trackRolling(rolling) {
        if (this.state.midi.midiMapMode === MAP) {
            this.store.dispatch(setActionMidiMapSelection(
                new TrackRollAction(this)));
        }

        this.store.dispatch(setTrackRolling(rolling));
        this.rollingManager.clear();
    }

    setSequenceRolling(
        sequenceRolling,
        resolution = this.rollingResolution,
        isAutomation=false,
        stepNumber=this.automationStep,
        force)
    {
        publishRoll(sequenceRolling, resolution.resolution, resolution.isTriplet);

        /*
          // THIS WAS AN EXPERIMENT OF WHEN HOLDING ROLL HAVING IT REWIND SO THAT
          // IT RESTARTS WHERE YOU STARTED THE ROLL RATHER THAN HOW THE BEAT WOULD
          // NORMALLY HIT HAD IT BEEN RUNNING THE WHOLE TIME
        if (sequenceRolling) {
            this.savedRollingStep = stepNumber;
        } else if (this.savedRollingStep !== undefined) {
            let numSteps = resolution.getNumberOfStepsAtBaseResolution();
            let mod = this.trackStep % numSteps;
            let saved = this.savedRollingStep % numSteps;
            console.log("diff = ", this.trackStep - this.savedRollingStep);
            let diff = this.trackStep - this.savedRollingStep;
            if (diff > 32) {
                this.seekToTrackStep(this.savedRollingStep - saved + mod);
            }
            this.savedRollingStep = undefined;
        }
        */
        
        if (this.state.midi.midiMapMode === MAP) {
            this.store.dispatch(setActionMidiMapSelection(
                new SequenceRollAction(this)));
        }


        if (!sequenceRolling) {
            this.schedulePatternBufferClear = true;
        } else if (sequenceRolling != this.isSequenceRolling || force) {
            let numSteps = resolution.getNumberOfStepsAtBaseResolution();
            stepNumber -= stepNumber % numSteps;
            for (let i in this.subSequences) {
                this.subSequences[i].initPatternBuffer(stepNumber);
                this.subSequences[i].tracks.forEach(track => track.initPatternBuffer(stepNumber));
            }
            if (isAutomation) {
                this.trackRolling = true;
            }
        }
        this.isSequenceRolling = sequenceRolling;

        if (isAutomation) {
            return;
        }

        if (this.store.getState().mix.isRecordingMix) {
            let resolution = this.resolution;
            this.mix.addEvent(new SequenceRollEvent(sequenceRolling, resolution));
        } else if (this.store.getState().mix.isPlayingMix) {
            this.mix.holdEvent(new SequenceRollEvent(sequenceRolling, this.resolution));
        }
    }

    isSequenceRollingAtStep(step) {
        if (this.store.getState().mix.isPlayingMix) {
            let sequenceRolling = this.mix.getValue(
                SEQUENCE_ROLL, step);
            let previousValue = this.mix.getValue(
                SEQUENCE_ROLL, step - 1);
            return sequenceRolling;
        }

        return this.isSequenceRolling;
    }

    // sequenceRolling is essentially "beat repeat" in other
    // sequencers/drum machines. it "rolls" the sequence at
    // whatever step it was when sequenceRolling was triggered`
    set sequenceRolling(sequenceRolling) {
        this.setSequenceRolling(sequenceRolling);
    }

    scheduleSequenceRollingIfNeeded(track, stepNumber, time, isSubSequence=false) {
        let state = this.store.getState();
        if (this.schedulePatternBufferClear) {
            track.patternRollStepNumber = undefined;
            this.schedulePatternBufferClear = false;
            return false;
        }

        if (!this.isSequenceRolling || (!isSubSequence && !track.rolling)) {
            // not needed since we're not "sequence rolling"
            return false;
        }

        if (!isSubSequence && track.subSequence.higherRolling !== undefined) {
            if (track.subSequence.higherRolling === false) {
                return false;
            }
        }
        track.updatePatternRollStepNumber(this.rollingResolution);

        return true;
    }

    set rollingResolution(resolution) {
        if (this.store.getState().mix.isRecordingMix) {
            let rollEvent = this.mix.getEvent(
                SEQUENCE_ROLL, this.automationStep);

            let resolutionChange = new ResolutionChangeEvent(resolution);
            resolutionChange.step = this.automationStep;
            rollEvent.addSubEvent(resolutionChange);
        } else if (this.state.mix.isPlayingMix) {
            // TODO: since we now use compound events to represent
            // a sequence roll + resolution change, we need to
            // somehow represent the hold
            this.mix.holdEvent(new ResolutionChangeEvent(resolution));
        }
    }


    set resolution(resolution) {
        if (this.resolution.isTriplet !== resolution.isTriplet) {
            for (let i in this.subSequences) {
                for (let j in this.subSequences[i].tracks) {
                    let t = this.subSequences[i].tracks[j];
                    if (t.pattern.needsSync) {
                        t.pattern.syncBuckets(this.resolution.isTriplet);
                    }
                }
            }
        }

        this.rollingResolution = resolution;
        if (this.state.midi.midiMapMode === MAP) {
            // we tell the midi mapping system that "resolution" was
            // changed so that the user can map a knob to it
            this.store.dispatch(setActionMidiMapSelection(
                new ResolutionAction(this, resolution)));
        }

                // dispatch to redux
        this.store.dispatch(setResolution(resolution));

        this.tracks.forEach(track => track.updatePatternRollStepNumber(resolution));
    }

    get rollingResolution() {
        if (this.store.getState().mix.isPlayingMix) {
            let rollEvent = this.mix.getEvent(
                SEQUENCE_ROLL, this.automationStep);
            if (rollEvent !== undefined) {
                let subEvent = rollEvent.getSubEvent(this.automationStep);
                return subEvent.getValue();
            }
        }

        return this.resolution;
    }

    get resolution() {
        return this.state.sequencer.resolution;
    }

    get displayResolution() {
        return this.state.sequencer.displayResolution;
    }
}
